import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { get, patch, post } from 'helpers/api_helper'
import { store as socketStore } from 'components/Common/Socket/store'
import { runPlayerCommand } from './PlayerAccounts/playerCreationOperation'
import { newPlayerOperationId, readPending, savePending } from './PlayerAccounts/playerInvitationState'

export const profileEditMessage = code => ({
  PROFILE_REVISION_CONFLICT: 'This profile changed since you opened it. Reload the current details before saving.',
  OPERATION_PAYLOAD_CONFLICT: 'This request already exists with different details. Check its saved result.',
  EMAIL_ALREADY_EXIST: 'Another account already uses this email address.',
  PROFILE_CURRENCY_BALANCE: 'Changing to a different currency requires a zero wallet balance.',
  ERROR_PERMISSIONS: 'Your account no longer has permission to edit this profile.',
  ACCOUNT_NOT_FOUND: 'This account is no longer available.',
  DELETED_ACCOUNT_READ_ONLY: 'Deleted accounts cannot be edited.',
}[code] || (code?.startsWith('INVALID_') ? 'Check the required details and current options before saving.' : 'The result is not confirmed. Check its status before retrying.'))

const effectKeys = ['indexStatus', 'organizerStatus', 'eventsStatus', 'bookingEmailStatus']
export const profileEffectsPending = result => effectKeys.some(key => ['pending', 'queued', 'sending', 'failed', 'unconfirmed'].includes(result[key]))
// Queued Events syncs and emails being sent complete on their own shortly after the save, unlike failed or unconfirmed ones.
export const profileEffectsInFlight = result => effectKeys.some(key => ['pending', 'queued', 'sending'].includes(result[key]))
const EFFECTS_CONFIRMATION_MS = 30000

export default function useProfileEditOperation({ actorId, id, kind }) {
  const { state: { socket: { socket, socketClientId } = {} } = {} } = useContext(socketStore)
  const key = `sportya-profile-edit:${actorId}:${kind}:${id}`
  const attempt = useRef(readPending(key))
  const active = useRef(null)
  const busy = useRef(false)
  const [outcome, setOutcome] = useState({ status: attempt.current ? 'unknown' : 'idle' })
  const run = useCallback(async (input, mode = 'check') => {
    if (busy.current || !input) return
    busy.current = true
    if (mode !== 'check') input = { ...input, correlationId: newPlayerOperationId() }
    attempt.current = input; savePending(key, input)
    setOutcome(current => mode === 'effects' ? { ...current, effectsBusy: true, effectsMessage: '' } : { status: 'pending' })
    // `confirm` only reads the saved receipt until its in-flight effects finish; the save's socket reply is already consumed.
    const track = async step => {
      const waiting = { effects: profileEffectsPending, confirm: profileEffectsInFlight }[step]
      const operation = runPlayerCommand({ ...input, accountId: id, operation: 'profile-edited', messageForCode: profileEditMessage,
        socket: step === 'confirm' ? undefined : socket, ...(step === 'confirm' ? { timeoutMs: EFFECTS_CONFIRMATION_MS } : {}),
        read: async operationId => {
          const response = await get('/admin/profile-edits/' + operationId)
          return waiting && response.data?.status === 'saved' && waiting(response.data) ? { data: { status: 'pending' } } : response
        },
        submit: ['retry', 'effects'].includes(step) ? () => post('/admin/profile-edits/' + input.operationId + '/retry', { socketClientId },
          { headers: { 'X-Correlation-Id': input.correlationId } }) : step === 'save' && input.payload ? () => patch(`/admin/${kind}-profiles/${id}`, {
          ...input.payload, operationId: input.operationId, socketClientId,
        }, { headers: { 'X-Correlation-Id': input.correlationId } }) : undefined,
      })
      active.current = operation
      const result = await operation.promise
      if (active.current !== operation || result.status === 'cancelled') return null
      active.current = null
      return result
    }
    let result = await track(mode)
    if (!result) return
    if (mode !== 'effects' && result.status === 'saved' && profileEffectsInFlight(result)) {
      setOutcome({ ...result, effectsBusy: true, effectsMessage: '' })
      const confirmed = await track('confirm')
      if (!confirmed) return
      // After the confirmation window the saved receipt still shows which updates remain unconfirmed.
      if (confirmed.status === 'saved') result = confirmed
    }
    busy.current = false
    if (mode === 'effects' && result.status !== 'saved') {
      setOutcome(current => ({ ...current, effectsBusy: false, effectsMessage: 'Secondary updates have not been confirmed. The profile remains saved.' })); return
    }
    if (result.code === 'PROFILE_EDIT_NOT_RECEIVED') {
      setOutcome({ status: 'resuming', message: 'Re-enter the original details to resume the same save request.' }); return
    }
    if (result.status === 'error' || (result.status === 'saved' && !profileEffectsPending(result))) { attempt.current = null; savePending(key, null) }
    setOutcome(result)
  }, [id, kind, key, socket, socketClientId])
  const started = useRef(false)
  useEffect(() => {
    if (!started.current) { started.current = true; if (attempt.current) run(attempt.current) }
  }, [run])
  useEffect(() => () => { const request = active.current; active.current = null; busy.current = false; request?.cancel() }, [])
  return { ...outcome, locked: ['pending', 'unknown'].includes(outcome.status),
    save: payload => run({ operationId: attempt.current?.operationId || newPlayerOperationId(), payload }, 'save'),
    check: () => run(attempt.current),
    resume: () => run(attempt.current, 'retry'),
    retryEffects: () => run(attempt.current, 'effects'),
    dismiss: () => { attempt.current = null; savePending(key, null) },
  }
}
